import { useEffect, useRef } from "react";
import { overLimit } from "@/components/ui/Note";
import { useSavedDraft } from "./use-save-attempt";

/**
 * How long after the last keystroke a self-saving field writes.
 *
 * Long enough that ordinary typing does not issue a write per character, short
 * enough that a save is not a thing the attorney waits for. One interval for
 * every field that saves this way, so they cannot drift.
 */
export const SAVE_DEBOUNCE_MS = 800;

/**
 * Write a draft once it settles, on blur, and on the way out.
 *
 * The field owns the draft and the adoption rule. This owns only the timer,
 * the unmount flush, a flush on demand, and the comparison that stops it
 * resending what is already stored. Words past `maxLength`, the server's limit,
 * are never sent: they would only be refused, and wait in the field until they fit.
 */
export function useDebouncedSave(
  draft: string,
  stored: string,
  onSave: (value: string) => void,
  maxLength?: number,
): { flush: () => void } {
  const save = (value: string) => {
    if (value === stored || overLimit(value, maxLength)) return;
    onSave(value);
  };

  const saveRef = useRef(save);
  saveRef.current = save;
  const draftRef = useRef(draft);
  draftRef.current = draft;

  useEffect(
    () => () => {
      saveRef.current(draftRef.current);
    },
    [],
  );

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (draft === stored) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      saveRef.current(draft);
    }, SAVE_DEBOUNCE_MS);
    return () => {
      if (timerRef.current === null) return;
      clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, [draft, stored]);

  const flush = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    save(draft);
  };
  return { flush };
}

/**
 * A field that saves itself: the draft that follows what is stored while
 * untouched, written once it settles, with the outcome of its last save.
 */
export function useSelfSavingDraft(
  stored: string,
  onSave: (value: string) => Promise<void>,
  maxLength?: number,
) {
  const { draft, setDraft, send, failed } = useSavedDraft(stored, onSave);
  const { flush } = useDebouncedSave(draft, stored, send, maxLength);
  return { draft, setDraft, flush, failed, retry: flush };
}
